
import { useEffect, useState } from 'react';
import { useReports } from '@/hooks/useReports';
import { useReportReliability } from '@/hooks/useReportReliability';
import { useEnhancedReliability } from '@/hooks/useEnhancedReliability';
import { ShareReportDialog } from './ShareReportDialog';
import { ScoreRadarChart } from './ScoreRadarChart';
import { ReferentDetailedCard } from './ReferentDetailedCard';
import { KeyIndicatorsCards } from './KeyIndicatorsCards';
import { CriticalResponses } from './CriticalResponses';
import { ThreeColumnInsights } from './ThreeColumnInsights';
import { ReportLoadingState } from './ReportLoadingState';
import { ReportNotFound } from './ReportNotFound';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
// import { generateReportPDF } from '@/utils/pdfGenerator'; // Ancienne méthode supprimée
import { logger } from '@/utils/productionLogger';
import { Button } from '@/components/ui/button';
import { Download, ArrowLeft, Share } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { parseAISynthesis, type CampaignReport } from '@/types/report';
import { computeRelationCheck } from '@/utils/relationValidation';

// Helper pour valider les données JSON/JSONB non vides
const isNonEmptyJson = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  
  // Si c'est une string, essayer de la parser
  if (typeof value === 'string') {
    if (value.trim() === '' || value === '{}' || value === '[]') return false;
    try {
      const parsed = JSON.parse(value);
      return isNonEmptyJson(parsed); // Récursion pour valider le contenu parsé
    } catch {
      return false;
    }
  }
  
  // Si c'est un objet
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return Object.keys(value).length > 0;
  }
  
  return false;
};

interface ReportViewerProps {
  reportId: string;
  onBack: () => void;
}

// Type pour le nouveau format structuré
interface StructuredItem {
  titre: string;
  description: string;
}

// Type pour supporter ancien et nouveau format
type AIItem = string | StructuredItem;

interface AISynthesis {
  resume: string;
  points_forts: AIItem[];
  axes_vigilance: AIItem[];
  conseils_manager: AIItem[];
  commentaire_global: string;
  email_company_matching?: {
    summary: {
      totalCount: number;
      okCount: number;
      warningCount: number;
      criticalCount: number;
      averageScore: number;
    };
    results: Array<{
      referentName: string;
      referentEmail: string;
      score: number;
      status: 'OK' | 'WARNING' | 'CRITICAL';
      reason: string;
      isCoherent: boolean;
    }>;
  };
}

export const ReportViewer = ({ reportId, onBack }: ReportViewerProps) => {
  const { getReport } = useReports();
  const { getReferentsFromCampaign, getCriticalResponses, getDateReliabilityAnalysis, getEmailCoherenceAnalysis, getCandidateFingerprint } = useReportReliability();
  const { calculateEnhancedReliability } = useEnhancedReliability();
  const { toast } = useToast();
  const [report, setReport] = useState<CampaignReport | null>(null);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aiSynthesis, setAiSynthesis] = useState<AISynthesis | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [referents, setReferents] = useState<any[]>([]);
  const [criticalResponses, setCriticalResponses] = useState<any>(null);
  const [reliabilityData, setReliabilityData] = useState<any>(null);
  const [candidateFingerprint, setCandidateFingerprint] = useState<any>(null);
  const [emailCoherenceResults, setEmailCoherenceResults] = useState<any[]>([]);
  const [selectedCriteria, setSelectedCriteria] = useState<any[]>([]);
  const [customCriteriaScores, setCustomCriteriaScores] = useState<Record<string, number>>({});

  /**
   * Transforme les données des référents pour le composant ReferentDetailedCard
   * Calcule les checks individuellement pour chaque référent
   */
  const prepareReferentData = (
    referentsData: any[], 
    reliabilityChecks: any,
    candidateFingerprint?: any,
    emailCoherenceResults?: any[],
    parsedAiSynthesis?: any // ✅ NOUVEAU : Paramètre pour éviter la race condition
  ) => {
    return referentsData.map(referent => {
      // ✅ NOUVEAU : Si le référent a refusé, retourner des checks grisés
      if (referent.status === 'Refusé') {
        return {
          id: referent.id,
          name: referent.name,
          position: referent.position || 'Poste non spécifié',
          company: referent.company || 'Entreprise inconnue',
          email: referent.email,
          status: referent.status,
          checks: {
            ipAddress: {
              title: 'Adresse IP',
              description: 'Refus référent',
              isValid: false,
              status: 'non-communique' as const
            },
            professionalEmail: {
              title: 'Email Professionnel',
              description: 'Refus référent',
              isValid: false,
              status: 'non-communique' as const
            },
            charter: {
              title: 'Charte',
              description: 'Refus référent',
              isValid: false,
              status: 'non-communique' as const
            },
            device: {
              title: 'Appareil',
              description: 'Refus référent',
              isValid: false,
              status: 'non-communique' as const
            },
            relationshipLevel: {
              title: 'Niveau de relation',
              description: 'Refus référent',
              isValid: false,
              status: 'non-communique' as const
            },
            dates: {
              title: 'Dates',
              description: 'Refus référent',
              isValid: false,
              status: 'non-communique' as const
            }
          }
        };
      }
      
      // Check 1: Adresse IP - Vérifier que l'IP existe et est différente du candidat
      const hasValidIP = referent.ipAddress !== null && referent.ipAddress !== undefined;
      const ipMatchesCandidate = candidateFingerprint?.ipAddress && 
        referent.ipAddress === candidateFingerprint.ipAddress;
      const ipIsValid = hasValidIP && !ipMatchesCandidate;
      
      // Déterminer le statut et la description de l'IP
      let ipStatus: 'conforme' | 'attention' | 'non-conforme';
      let ipDescription: string;
      
      if (ipIsValid) {
        ipStatus = 'conforme';
        ipDescription = 'adresse IP unique';
      } else if (ipMatchesCandidate) {
        ipStatus = 'non-conforme';
        ipDescription = 'IP identique au candidat';
      } else {
        ipStatus = 'attention';
        ipDescription = 'adresse IP non identifiée';
      }

      // Check 2: Email Professionnel - Vérifier cohérence avec entreprise
      // Note: Tous les emails sont nécessairement professionnels (validation en amont)
      
      // ✅ CORRECTION : Utiliser la logique stricte pour la cohérence email
      const emailCompanyResults = parsedAiSynthesis?.email_company_matching?.results || [];
      const coherenceResult = emailCompanyResults.find((r: any) => r.referentEmail === referent.email);
      
      // Fallback vers l'ancien système si pas de données dans ai_synthesis
      const fallbackResult = emailCoherenceResults?.find(r => r.referentEmail === referent.email);
      
      // ✅ LOGIQUE STRICTE DIRECTE : Vérifier cohérence nom/email
      let isCoherent = false;
      let emailDescription = 'email incohérent avec l\'entreprise';
      
      if (coherenceResult) {
        // Utiliser les données de l'Edge Function (plus récentes et strictes)
        isCoherent = coherenceResult.isCoherent;
        emailDescription = coherenceResult.reason || 'email professionnel validé';
      } else {
        // ✅ CALCUL DIRECT avec logique stricte (réplique de emailCompanyCoherence.ts)
        const nameParts = (referent.name || '').trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        // Vérifier cohérence nom/email (logique stricte)
        const emailLocal = referent.email.split('@')[0].toLowerCase();
        const normalizedFirstName = firstName.toLowerCase().replace(/[^a-z]/g, '');
        const normalizedLastName = lastName.toLowerCase().replace(/[^a-z]/g, '');
        
        const hasFirstName = normalizedFirstName.length > 2 && emailLocal.includes(normalizedFirstName);
        const hasLastName = normalizedLastName.length > 2 && emailLocal.includes(normalizedLastName);
        
        let nameScore = 0;
        if (hasFirstName && hasLastName) nameScore = 100;
        else if (hasLastName) nameScore = 65;
        else if (hasFirstName) nameScore = 60;
        
        // Vérifier cohérence domaine/entreprise
        const domain = referent.email.split('@')[1]?.toLowerCase() || '';
        const company = (referent.company || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const domainCompany = domain.split('.')[0];
        const domainMatchesCompany = domainCompany.includes(company) || company.includes(domainCompany);
        
        // LOGIQUE STRICTE : Si nom incohérent (< 50) ET travaille encore → NON CONFORME
        if (nameScore < 50 && referent.referent_still_works_at_company !== false) {
          isCoherent = false;
          emailDescription = 'email incohérent: nom ne correspond pas à l\'adresse email';
        } else if (!domainMatchesCompany && referent.referent_still_works_at_company !== false) {
          // Si domaine ne correspond pas ET travaille encore → SUSPECT
          isCoherent = false;
          emailDescription = 'email incohérent: domaine ne correspond pas à l\'entreprise déclarée';
        } else if (referent.referent_still_works_at_company === false) {
          // ✅ CORRECTION : Vérifier le nameScore MÊME pour les anciens employés
          if (nameScore < 50) {
            isCoherent = false;
            emailDescription = 'email incohérent: nom ne correspond pas à l\'adresse email';
          } else {
            // Ancien employé + nom cohérent = ACCEPTABLE
            isCoherent = true;
            emailDescription = 'email professionnel différent (ancien employé)';
          }
        } else {
          // Autres cas → CONFORME
          isCoherent = true;
          emailDescription = 'email cohérent avec l\'entreprise';
        }
        
        // Fallback pour affichage si pas de données Edge Function
        if (fallbackResult && !coherenceResult) {
          const isFormerEmployee = fallbackResult.details?.stillWorksAtCompany === false;
          if (fallbackResult.isCoherent && isFormerEmployee) {
            emailDescription = 'email professionnel différent (ancien employé)';
          }
        }
      }
      
      const emailStatus: 'conforme' | 'non-conforme' = isCoherent ? 'conforme' : 'non-conforme';

      // Check 3: Charte - Vérifier signature
      const charterIsValid = referent.hasSigned;
      const charterStatus: 'conforme' | 'non-conforme' = charterIsValid ? 'conforme' : 'non-conforme';
      const charterDescription = charterIsValid ? 'charte signée' : 'charte non signée';

      // Check 4: Appareil - Vérifier que le device existe et est différent du candidat
      const hasValidDevice = referent.deviceFingerprint !== null && 
        referent.deviceFingerprint !== undefined;
      const deviceMatchesCandidate = candidateFingerprint?.deviceFingerprint && 
        referent.deviceFingerprint === candidateFingerprint.deviceFingerprint;
      const deviceIsValid = hasValidDevice && !deviceMatchesCandidate;
      
      let deviceStatus: 'conforme' | 'attention' | 'non-conforme';
      let deviceDescription: string;
      
      if (deviceIsValid) {
        deviceStatus = 'conforme';
        deviceDescription = 'appareils diversifiés';
      } else if (deviceMatchesCandidate) {
        deviceStatus = 'non-conforme';
        deviceDescription = 'appareil identique au candidat';
      } else {
        deviceStatus = 'attention';
        deviceDescription = 'appareil non identifié';
      }

      // Check 5: Niveau de relation - Validation avec normalisation
      const relationCheck = computeRelationCheck(
        referent.relation_type,
        referent.relation_type_confirmed
      );
      
      const relationshipIsValid = relationCheck.status === 'conforme';
      const relationshipStatus = relationCheck.status;
      const relationshipDescription = relationCheck.message;

      // Check 6: Dates - NOUVELLE LOGIQUE : Priorité candidat ↔ référent
      // ✅ CORRECTION : Déclarer les variables EN DEHORS de tous les blocs conditionnels
      let datesAreValid = false;
      let datesStatus: 'conforme' | 'non-conforme' = 'non-conforme';
      let datesDescription = 'données de dates insuffisantes';
      
      // ✅ CORRECTION : Récupérer les données depuis les nouveaux champs exposés
      const candidateStartDate = referent.candidate_collaboration_start_date;
      const candidateEndDate = referent.candidate_collaboration_end_date;
      const candidateStillWorking = referent.candidate_still_working_together;
      
      // ✅ CORRECTION : Récupérer les données référent depuis les champs exposés directement
      let referentStartDate = referent.referent_collaboration_start_date;
      let referentEndDate = referent.referent_collaboration_end_date;
      let referentStillWorking = referent.referent_still_working_together; // ✅ CORRECTION : Utiliser le bon champ
      
      // ✅ FALLBACK : Essayer de récupérer depuis parsedAiSynthesis si pas de données directes
      if (!referentStartDate && parsedAiSynthesis?.referent_responses) {
        const referentResponse = parsedAiSynthesis.referent_responses.find((r: any) => r.referent_id === referent.id);
        if (referentResponse) {
          referentStartDate = referentResponse.collaboration_start_date;
          referentEndDate = referentResponse.collaboration_end_date;
          referentStillWorking = referentResponse.still_working_together; // ✅ CORRECTION : Utiliser le bon champ
        }
      }
      
      // ✅ PRIORITÉ : Si candidat ET référent ont des dates → calcul individuel
      if (candidateStartDate && referentStartDate) {
        // LOGIQUE STRICTE V2 : Écart > 30 jours = NON CONFORME
        try {
          const candidateStart = new Date(candidateStartDate);
          const referentStart = new Date(referentStartDate);
          
          // Calculer l'écart en jours pour dates de début
          const startDaysDiff = Math.abs((candidateStart.getTime() - referentStart.getTime()) / (1000 * 60 * 60 * 24));
          
          // Vérifier dates de fin si disponibles
          let endDaysDiff = 0;
          if (candidateEndDate && referentEndDate) {
            const candidateEnd = new Date(candidateEndDate);
            const referentEnd = new Date(referentEndDate);
            endDaysDiff = Math.abs((candidateEnd.getTime() - referentEnd.getTime()) / (1000 * 60 * 60 * 24));
          }
          
          // Vérifier cohérence des statuts "en cours"
          const stillWorkingMismatch = candidateStillWorking !== referentStillWorking;
          
          // LOGIQUE STRICTE : Écart > 30 jours OU désaccord "travaille encore" = NON CONFORME
          if (startDaysDiff > 30 || endDaysDiff > 30 || stillWorkingMismatch) {
            datesAreValid = false;
            datesStatus = 'non-conforme';
            
            // Message détaillé selon le problème
            if (stillWorkingMismatch) {
              datesDescription = `incohérence candidat ↔ référent (désaccord sur statut actuel)`;
            } else if (startDaysDiff > 30 && endDaysDiff > 30) {
              datesDescription = `incohérence candidat ↔ référent (écart début: ${Math.round(startDaysDiff)} jours, fin: ${Math.round(endDaysDiff)} jours)`;
            } else if (startDaysDiff > 30) {
              datesDescription = `incohérence candidat ↔ référent (écart début: ${Math.round(startDaysDiff)} jours)`;
            } else {
              datesDescription = `incohérence candidat ↔ référent (écart fin: ${Math.round(endDaysDiff)} jours)`;
            }
          } else {
            datesAreValid = true;
            datesStatus = 'conforme';
            datesDescription = `cohérence candidat ↔ référent (écart: ${Math.round(startDaysDiff)} jours)`;
          }
        } catch (_error) {
          // Erreur de parsing des dates
          datesAreValid = false;
          datesStatus = 'non-conforme';
          datesDescription = 'erreur analyse des dates';
        }
      } else if (reliabilityChecks) {
        // ✅ FALLBACK GLOBAL : Seulement si pas de données individuelles
        datesAreValid = reliabilityChecks.dateCoherence || false;
        datesStatus = datesAreValid ? 'conforme' : 'non-conforme';
        datesDescription = datesAreValid ? 'cohérence temporelle' : 'incohérence temporelle';
      }

      // ✅ NOUVEAU : Enrichissement avec les données des expériences
      // Récupérer les expériences depuis parsedAiSynthesis pour le matching
      const experiences = parsedAiSynthesis?.experiences || [];
      const matchingExp = experiences.find((exp: any) => exp.id === referent.experience_id);
      
      // Utiliser les vraies données d'expérience si disponibles, sinon fallback
      const enrichedPosition = matchingExp?.position || referent.position || 'Poste non spécifié';
      const enrichedCompany = matchingExp?.company || referent.company || 'Entreprise inconnue';

      return {
        id: referent.id,
        name: referent.name,
        position: enrichedPosition, // ✅ Position enrichie
        company: enrichedCompany,   // ✅ Entreprise enrichie
        email: referent.email,
        status: referent.status,
        // ✅ NOUVEAU : Données originales pour debug/fallback
        originalPosition: referent.position,
        originalCompany: referent.company,
        experienceId: referent.experience_id,
        checks: {
          ipAddress: {
            title: 'Adresse IP',
            description: ipDescription,
            isValid: ipIsValid,
            status: ipStatus
          },
          professionalEmail: {
            title: 'Email Professionnel',
            description: emailDescription,
            isValid: emailStatus === 'conforme',
            status: emailStatus
          },
          charter: {
            title: 'Charte',
            description: charterDescription,
            isValid: charterIsValid,
            status: charterStatus
          },
          device: {
            title: 'Appareil',
            description: deviceDescription,
            isValid: deviceIsValid,
            status: deviceStatus
          },
          relationshipLevel: {
            title: 'Niveau de relation',
            description: relationshipDescription,
            isValid: relationshipIsValid,
            status: relationshipStatus
          },
          dates: {
            title: 'Dates',
            description: datesDescription,
            isValid: datesAreValid,
            status: datesStatus
          }
        }
      };
    });
  };

  useEffect(() => {
    let isMounted = true;

    const loadReport = async () => {
      if (!reportId) {
        setError('Report ID manquant');
        setLoading(false);
        return;
      }

      try {
        setError(null);

        const reportData = await getReport(reportId);
        if (!isMounted) return;

        // Vérifier que les données sont valides et ne contiennent pas d'erreur
        if (!reportData || typeof reportData !== 'object' || 'error' in reportData) {
          setError('Rapport introuvable');
          setLoading(false);
          return;
        }

        // Typer correctement reportData comme CampaignReport après validation
        const typedReportData = reportData as CampaignReport;
        setReport(typedReportData);

        // 🔧 VARIABLE LOCALE pour éviter la race condition React
        let criteriaAverages: Record<string, number> = {};

        // Load campaign selected criteria from the report's campaign data
        if (typedReportData.campaigns?.selected_evaluation_criteria &&
          Array.isArray(typedReportData.campaigns.selected_evaluation_criteria)) {
          setSelectedCriteria(typedReportData.campaigns.selected_evaluation_criteria);

          // Calculate custom criteria scores from referent responses
          if (typedReportData.campaign_id) {
            const { data: responses } = await supabase
              .from('referent_responses')
              .select('custom_criteria_scores')
              .eq('campaign_id', typedReportData.campaign_id)
              .not('custom_criteria_scores', 'is', null);

            if (responses && responses.length > 0) {
              // 🔧 ASSIGNER À LA VARIABLE LOCALE (pas seulement au state)
              criteriaAverages = {};

              // Calculate average for each custom criterion
              typedReportData.campaigns?.selected_evaluation_criteria?.forEach((criterion: any) => {
                const scores: number[] = [];

                responses.forEach((response: any) => {
                  if (response.custom_criteria_scores && response.custom_criteria_scores[criterion.id]) {
                    scores.push(response.custom_criteria_scores[criterion.id]);
                  }
                });

                if (scores.length > 0) {
                  criteriaAverages[criterion.id] = scores.reduce((sum, score) => sum + score, 0) / scores.length;
                }
              });

              setCustomCriteriaScores(criteriaAverages); // ✅ Pour l'affichage frontend
            }
          }
        }

        if (typedReportData.ai_synthesis) {
          try {
            const synthesis = typeof typedReportData.ai_synthesis === 'string' 
              ? JSON.parse(typedReportData.ai_synthesis) 
              : typedReportData.ai_synthesis;
            setAiSynthesis(synthesis);
          } catch (e) {
            console.error('Erreur parsing AI synthesis:', e);
          }
        }

        // Load real referents data only if campaign_id exists
        if (typedReportData.campaign_id) {

          try {
            // Parse AI synthesis to get confidence score
            let parsedAiSynthesis = null;
            if (typedReportData.ai_synthesis) {
              try {
                parsedAiSynthesis = typeof typedReportData.ai_synthesis === 'string'
                  ? JSON.parse(typedReportData.ai_synthesis)
                  : typedReportData.ai_synthesis;
              } catch (e) {
                console.error('Erreur parsing AI synthesis:', e);
              }
            }

            const [referentsData, criticalData, dateReliabilityData, emailCoherenceData, candidateFingerprintData] = await Promise.all([
              getReferentsFromCampaign(typedReportData.campaign_id),
              getCriticalResponses(typedReportData.campaign_id, parsedAiSynthesis),
              getDateReliabilityAnalysis(typedReportData.campaign_id),
              getEmailCoherenceAnalysis(typedReportData.campaign_id),
              getCandidateFingerprint(typedReportData.campaign_id)
            ]);

            if (!isMounted) return;

            // 🔍 DEBUG: Log des données chargées
            

            setReferents(referentsData);
            setCriticalResponses(criticalData);
            setCandidateFingerprint(candidateFingerprintData);
            setEmailCoherenceResults(emailCoherenceData.results || []);

            // Calculate enhanced reliability with Viguen corrections
            const enhancedReliability = await calculateEnhancedReliability(typedReportData.campaign_id);
            setReliabilityData(enhancedReliability);

            // 💾 SAUVEGARDER LES MÉTRIQUES EN BASE DE DONNÉES
            if (enhancedReliability) { // ✅ Condition simplifiée
              try {
                const { data: existingReport } = await supabase
                  .from('campaign_reports')
                  .select('id, metrics_computed_at, metrics_version, custom_criteria_averages, referent_enriched_data')
                  .eq('campaign_id', typedReportData.campaign_id)
                  .single();

                if (existingReport) {
                  // ✅ VALIDATION COMPLÈTE : Vérifier que TOUTES les données critiques sont présentes
                  const hasValidCache = 
                    existingReport.metrics_computed_at !== null &&
                    existingReport.metrics_version === 'v11_exclude_refused_referents' && // ✅ NOUVELLE VERSION : Exclusion référents refusés
                    isNonEmptyJson(existingReport.custom_criteria_averages) &&
                    isNonEmptyJson(existingReport.referent_enriched_data);

                  if (hasValidCache) {
                    // Cache complet validé, skip sauvegarde
                  } else {
                    // ✅ Cache incomplet ou absent, procéder à la sauvegarde
                    // ✅ NOUVEAU : Préparer les checks individuels pour sauvegarde
                    const referentChecks = prepareReferentData(referentsData, enhancedReliability.checks, candidateFingerprintData, emailCoherenceData.results || [], parsedAiSynthesis);
                    
                    // Extraire seulement les données nécessaires pour la base
                    const referentChecksForDB = referentChecks.map(referent => ({
                      id: referent.id,
                      name: referent.name,
                      email: referent.email,
                      status: referent.status,
                      checks: referent.checks
                    }));

                    const { error: saveError } = await supabase
                      .from('campaign_reports')
                      .update({
                        // ✅ Scores agrégés (déjà présents)
                        reliability_score: enhancedReliability.score,
                        critical_responses: criticalData, // ✅ JSONB : objet natif
                        custom_criteria_averages: criteriaAverages, // ✅ JSONB : objet natif (variable locale)
                        overall_avg: typedReportData.overall_avg,
                        // ❌ SUPPRIMÉ : sentiment_distribution (owned par Edge Function uniquement)
                        email_coherence_score: emailCoherenceData.overallScore,
                        date_reliability_score: dateReliabilityData.averageScore,
                        relation_conformity_score: parsedAiSynthesis?.relation_conformity_score || 0,
                        
                        // ✅ Checks individuels (JSONB : arrays/objets natifs)
                        referent_individual_checks: referentChecksForDB, // ✅ JSONB : array natif
                        relation_conformity_checks: parsedAiSynthesis?.relation_conformity_checks || [], // ✅ JSONB : array natif
                        
                        // ✅ NOUVEAU : Données enrichies des référents pour PDF (JSONB : array natif)
                        referent_enriched_data: referentChecks, // ✅ JSONB : array natif complet
                        
                        // 🆕 AJOUT : Objets détaillés complets pour PDF (JSONB : objets natifs sérialisés)
                        reliability_checks: JSON.parse(JSON.stringify(enhancedReliability.checks || {})), // ✅ JSONB : objet sérialisé
                        email_coherence_results: JSON.parse(JSON.stringify(emailCoherenceData || {})), // ✅ JSONB : objet sérialisé
                        date_reliability_analysis: JSON.parse(JSON.stringify(dateReliabilityData || {})), // ✅ JSONB : objet sérialisé
                        device_fingerprint_analysis: JSON.parse(JSON.stringify(candidateFingerprintData || {})), // ✅ JSONB : objet sérialisé
                        
                        // Métadonnées
                        metrics_computed_at: new Date().toISOString(),
                        metrics_version: 'v11_exclude_refused_referents' // ✅ VERSION MISE À JOUR : Exclusion référents refusés
                      })
                      .eq('id', existingReport.id);

                    if (saveError) {
                      logger.error('❌ Erreur sauvegarde métriques:', saveError);
                    } else {
                      // ✅ Métriques et checks individuels sauvegardés
                    }
                  }
                }
              } catch (saveError) {
                console.error('Erreur lors de la sauvegarde:', saveError);
              }
            }
          } catch (dataError) {
            console.error('Error loading additional data:', dataError);
            // Don't fail the entire component if additional data fails
          }
        }

      } catch (_error) {
        console.error('Erreur chargement rapport:', error);
        if (isMounted) {
          setError('Erreur lors du chargement du rapport');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]); // Retrait des dépendances qui causent la boucle infinie

  const handleDownloadPDF = async () => {
    if (!report?.campaigns?.candidate_name) {
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le rapport : données manquantes",
        variant: "destructive",
      });
      return;
    }

    setIsDownloadingPDF(true);

    try {
      // ✅ Utiliser la nouvelle Edge Function generate-report-pdf avec Gotenberg
      const { data, error } = await supabase.functions.invoke('generate-report-pdf', {
        body: {
          reportId: report.id,
          recruiterMessage: null, // Pas de message pour le téléchargement recruteur
          sendToCandidate: false, // ✅ Paramètre correct : pas d'envoi d'email, juste génération PDF
          anonymize: true // ✅ NOUVEAU : Anonymisation RGPD pour téléchargements recruteur
        },
      });

      if (error) throw error;

      // ✅ L'Edge Function retourne pdfUrl, pas pdfBase64
      if (data?.pdfUrl) {
        // Télécharger le PDF depuis l'URL
        const response = await fetch(data.pdfUrl);
        if (!response.ok) throw new Error('Erreur téléchargement PDF');
        
        const blob = await response.blob();
        
        // Créer un lien de téléchargement
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `rapport-${report.campaigns?.candidate_name?.replace(/\s+/g, '-').toLowerCase() || 'candidat'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('URL du PDF non disponible dans la réponse');
      }
      
      toast({
        title: "PDF généré avec succès",
        description: "Le rapport a été téléchargé",
        variant: "default"
      });
    } catch (downloadError) {
      console.error('Erreur génération PDF recruteur:', downloadError);
      toast({
        title: "Erreur",
        description: "Impossible de générer le PDF. Veuillez réessayer.",
        variant: "destructive"
      });
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  // Use default values if data not loaded yet
  const defaultCritical = {
    wouldRecommend: [
      { referentName: 'Dimitri NDEKE NGAKUBA', value: true },
      { referentName: 'Dimitri NDEKE NGAKUBA', value: null }
    ],
    wouldRehire: [
      { referentName: 'Dimitri NDEKE NGAKUBA', value: true },
      { referentName: 'Dimitri NDEKE NGAKUBA', value: null }
    ],
    hasIncidents: false,
    confidenceLevel: 75
  };

  if (loading) {
    return <ReportLoadingState />;
  }

  if (error) {
    return <ReportNotFound onBack={onBack} />;
  }

  if (!report) {
    return <ReportNotFound onBack={onBack} />;
  }

  return (
    <div className="space-y-8">
      {/* Boutons de navigation en dehors du PDF */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 no-print">
        <Button variant="ghost" onClick={onBack} className="self-start">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux rapports
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShareDialogOpen(true)}>
            <Share className="h-4 w-4 mr-2" />
            Partager par email
          </Button>
          <Button onClick={handleDownloadPDF} disabled={isDownloadingPDF}>
            <Download className="h-4 w-4 mr-2" />
            {isDownloadingPDF ? 'Génération...' : 'Télécharger PDF'}
          </Button>
        </div>
      </div>

      {/* Contenu principal du rapport avec ID pour la capture */}
      <div id="report-viewer-content" className="space-y-8 bg-white p-6 rounded-lg print-optimized">
        {/* Header du rapport inclus dans le PDF */}
        <div className="w-full px-4 py-5 bg-gray-50 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-zinc-200 inline-flex justify-between items-start">
          <div className="flex-1 inline-flex flex-col justify-start items-start gap-2">
            <div className="self-stretch justify-start text-gray-800 text-3xl font-semibold font-['Bricolage_Grotesque']">
              {report.campaigns?.candidate_name}
            </div>
            <div className="self-stretch justify-start text-teal-400 text-base font-medium font-['Bricolage_Grotesque']">
              {report.campaigns?.target_position}
            </div>
            <div className="self-stretch justify-start text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
              Généré le {format(new Date(report.generated_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
            </div>
            <div className="self-stretch justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
              #{report.id.substring(0, 8).toUpperCase()}
            </div>
          </div>
        </div>

        {/* Cartes d'indicateurs clés */}
        {(() => {
          const synthesis = parseAISynthesis(report.ai_synthesis);
          
          // Lire le sentiment depuis la base de données plutôt que depuis l'AI synthesis
          let sentimentDistribution = { positive: 0, neutral: 0, negative: 0 };
          if (report.sentiment_distribution) {
            try {
              // ✅ FIX : Gérer les deux cas (objet JSONB natif ou string JSON)
              const parsedSentiment = typeof report.sentiment_distribution === 'string' 
                ? JSON.parse(report.sentiment_distribution)
                : report.sentiment_distribution;
                
              // ✅ Vérifier que l'objet contient des données valides
              if (parsedSentiment && typeof parsedSentiment === 'object' && Object.keys(parsedSentiment).length > 0) {
                sentimentDistribution = {
                  positive: parsedSentiment.positif || 0,
                  neutral: parsedSentiment.neutre || 0,
                  negative: parsedSentiment.negatif || 0
                };
              }
            } catch (e) {
              console.warn('Erreur parsing sentiment_distribution:', e);
            }
          }
          
          return (
            <KeyIndicatorsCards
              reliabilityScore={reliabilityData?.score || 0}
              overallAverage={report.overall_avg || 0}
              confidenceScore={synthesis.score_confiance || 0}
              sentimentDistribution={sentimentDistribution}
              hasEnoughDataForScores={reliabilityData?.hasEnoughDataForScores ?? true}
            />
          );
        })()}

        {/* Référents sollicités avec vérifications de sécurité */}
        {referents.length > 0 && reliabilityData && (
          <ReferentDetailedCard 
            referents={prepareReferentData(referents, reliabilityData.checks, candidateFingerprint, emailCoherenceResults, aiSynthesis)} 
          />
        )}



        <ScoreRadarChart
          scores={customCriteriaScores}
          criteria={selectedCriteria}
          overall={report.overall_avg || 0}
        />

        <CriticalResponses responses={criticalResponses || defaultCritical} />

        {/* Analyse approfondie en 3 colonnes */}
        {aiSynthesis && (
          <ThreeColumnInsights
            resume={aiSynthesis.resume}
            pointsForts={
              Array.isArray(aiSynthesis.points_forts)
                ? aiSynthesis.points_forts.map(item => 
                    typeof item === 'string' 
                      ? { titre: item, description: '' }
                      : item
                  )
                : []
            }
            axesAmelioration={
              Array.isArray(aiSynthesis.axes_vigilance)
                ? aiSynthesis.axes_vigilance.map(item => 
                    typeof item === 'string' 
                      ? { titre: item, description: '' }
                      : item
                  )
                : []
            }
            conseilsManager={
              Array.isArray(aiSynthesis.conseils_manager)
                ? aiSynthesis.conseils_manager.map(item => 
                    typeof item === 'string' 
                      ? { titre: item, description: '' }
                      : item
                  )
                : []
            }
          />
        )}
      </div>

      <ShareReportDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        report={report}
      />
    </div>
  );
};
